import { DEBUG_NAMESPACE, useDebug } from '@/composables/useDebug';
import { defineStore } from 'pinia';

const insightsZipFilename = 'insights.zip';

export enum ArtifactStatus {
  PENDING = 'pending',
  COMPLETED = 'completed',
}

export interface PublicBucketUrl {
  filename: string;
  url: string;
}

export interface Artifact {
  id: string;
  type: string;
  status: ArtifactStatus;
  createdAt: string;
  updatedAt: string;
  content: string | null;
  publicBucketUrls: PublicBucketUrl[];
  itemId: string;
}

export interface ArtifactStoreState {
  list: Artifact[];
}

const { debug } = useDebug(DEBUG_NAMESPACE.STORE_ARTIFACTS);

export const useArtifactsStore = defineStore('artifactsStore', {
  state: () =>
    ({
      list: [],
    }) as ArtifactStoreState,
  actions: {
    /**
     * Initialize the store with artifacts data.
     * @param artifacts - Array of artifacts data.
     */
    initialize(artifacts: any[]) {
      debug('[initialize] Initialize Artifacts store', artifacts);
      if (artifacts && artifacts.length > 0) {
        const mappedArtifacts = artifacts.map(artifact => ({
          id: artifact.data.id,
          type: artifact.data.attributes.type,
          status: artifact.data.attributes.status,
          createdAt: artifact.data.attributes.createdAt,
          updatedAt: artifact.data.attributes.updatedAt,
          content: artifact.data.attributes.content,
          publicBucketUrls: artifact.data.attributes.publicBucketUrls || [],
          itemId: artifact.data.relationships?.item?.data?.id || '',
        }));
        this.setArtifacts(mappedArtifacts);
      }
    },
    /**
     * Set a list of artifacts into the store.
     * @param artifacts - Mapped list of artifacts.
     */
    setArtifacts(artifacts: Artifact[]) {
      if (!artifacts || artifacts.length === 0) {
        debug('Cannot set artifacts data', artifacts);
        return;
      }

      this.$patch({ list: artifacts });
    },
  },
  getters: {
    insightsURL: (state): string =>
      // Filter completed Insights artifacts with created attachments (filename is hardcoded at backend)
      state.list
        .filter(
          ({ type, status, publicBucketUrls }) =>
            type === 'Insights' && status === ArtifactStatus.COMPLETED && publicBucketUrls.some(({ filename }) => filename === insightsZipFilename)
        )
        .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
        .at(0)
        ?.publicBucketUrls.find(({ filename }) => filename === insightsZipFilename)?.url || '',
  },
});
